import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import client from "../api/client";

export const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const MAX_UPLOAD_MB = 15;

// Library cards only know the small gallery thumbnail; the editor swaps in the
// sharper transparent board preview once the server has returned it.
export const catalogAsset = (image) => ({
  source: "catalog",
  ref: image.file_id,
  url: image.thumbnail_path,
  width: image.width,
  height: image.height,
  name: image.file_name,
  transparent: /\.png$/i.test(image.thumbnail_path || ""),
  provisional: true,
});

export const uploadAsset = (upload) => ({
  source: "upload",
  ref: upload.id,
  url: upload.url,
  width: upload.width,
  height: upload.height,
  name: upload.name,
  transparent: Boolean(upload.transparent),
});

export default function useUploads({ onRemoved } = {}) {
  const onRemovedRef = useRef(onRemoved);
  onRemovedRef.current = onRemoved;
  const [items, setItems] = useState([]);
  const [limit, setLimit] = useState(null);
  const [backgroundRemoval, setBackgroundRemoval] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(0);

  useEffect(() => {
    client
      .get("/api/uploads")
      .then(({ data }) => {
        setItems(data.items);
        setLimit(data.limit);
        setBackgroundRemoval(Boolean(data.background_removal));
      })
      .catch((err) => {
        console.error("Uploads failed:", err);
        toast.error("Could not load your uploaded images.");
      })
      .finally(() => setLoading(false));
  }, []);

  const upload = useCallback(async (files) => {
    const valid = files.filter((file) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: only JPEG, PNG and WebP images are supported.`);
        return false;
      }
      if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
        toast.error(`${file.name} is larger than ${MAX_UPLOAD_MB} MB.`);
        return false;
      }
      return true;
    });

    const added = [];
    setPending((count) => count + valid.length);
    // One at a time: kinder to the server and keeps the order the user chose.
    for (const file of valid) {
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await client.post("/api/uploads", form);
        setItems((current) => [data, ...current]);
        added.push(uploadAsset(data));
      } catch (err) {
        toast.error(err.response?.data?.error || `Could not upload ${file.name}.`);
      } finally {
        setPending((count) => count - 1);
      }
    }
    return added;
  }, []);

  const remove = useCallback(async (id) => {
    try {
      await client.delete(`/api/uploads/${id}`);
      setItems((current) => current.filter((item) => item.id !== id));
      onRemovedRef.current?.(id);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not delete the image.");
    }
  }, []);

  // Makes a transparent copy; the original upload is left untouched.
  const removeBackground = useCallback(async (id) => {
    setRemoving(id);
    try {
      const { data } = await client.post(`/api/uploads/${id}/remove-background`, null, { timeout: 120000 });
      setItems((current) => [data, ...current]);
      toast.success("Background removed. The cut-out was added to My uploads.");
      return uploadAsset(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not remove the background.");
      return null;
    } finally {
      setRemoving(null);
    }
  }, []);

  return { items, limit, loading, pending, upload, remove, backgroundRemoval, removing, removeBackground };
}
